// Story 7.1 — create-account form rendered inside the LandingPage
// AuthCard. Three fields (email + password + ToS consent), plus an
// access-code field when the closed-beta signup gate is active (GET
// /api/v1/auth/signup-policy → access_code_required). Fetches the
// currently-active ToS version_label from GET /api/v1/legal/current-tos
// on mount and includes it in the register body.
//
// Story 6.18 (follow-up F7) added the hCaptcha widget and the
// `hcaptcha_token` it produces. The widget is the VISIBLE half of the bot
// gate and on its own gates nothing — the enforcing half is
// `captcha_service` on the server, which verifies the token against
// hCaptcha's siteverify whenever HCAPTCHA_SECRET is set. That is why this
// form never blocks submit on an unsolved captcha: it has no authority to.
//
// On 201 success the backend does NOT set a session cookie — verify-
// email must land first. We redirect to /verify-email-pending and
// show a success toast.

import { useRef, useState, type FormEvent } from "react";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Input, Label } from "@greenhold/components";

import { resolveHcaptchaSiteKey } from "../../lib/hcaptcha";

import { useCreateAccount, type CreateAccountError } from "./useCreateAccount";
import { useSignIn } from "./useSignIn";
import { useCurrentTos } from "./useCurrentTos";
import { useSignupPolicy } from "./useSignupPolicy";

interface Props {
  /** Called with the entered email if the user hits the "Sign in
   *  instead" link on the 409 already-registered error. */
  onSwitchToSignIn?: (email: string) => void;
}

// Story 6.18 (follow-up F7) — the site key, resolved through the SAME module
// `vite.config.ts` uses to refuse a production build with a placeholder or a
// TEST key. One definition, two consumers: the runtime fallback that keeps
// local dev working, and the build-time rule that keeps the fallback from
// reaching production. See `src/lib/hcaptcha.ts`.
function hcaptchaSiteKey(): string {
  return resolveHcaptchaSiteKey(
    import.meta.env.REACT_APP_HCAPTCHA_SITE_KEY as string | undefined,
  );
}

export function CreateAccountForm({ onSwitchToSignIn }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tos = useCurrentTos();
  const signupPolicy = useSignupPolicy();
  const createAccount = useCreateAccount();
  const signIn = useSignIn();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accessCode, setAccessCode] = useState("");
  // Forced on after a 403 invalid_access_code, so the field shows even
  // when the policy fetch failed (fail-open) but the gate is active.
  const [accessCodeForced, setAccessCodeForced] = useState(false);
  const [tosAccepted, setTosAccepted] = useState(false);
  const [error, setError] = useState<CreateAccountError | null>(null);
  // Story 6.18 (follow-up F7). The token is NOT a submit precondition here:
  // whether it is required is the backend's call (it verifies server-side iff
  // HCAPTCHA_SECRET is set), and a client-side block would only stop honest
  // users — the bot F7 is about never renders this form at all.
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaRef = useRef<HCaptcha | null>(null);

  const accessCodeRequired =
    signupPolicy.data?.access_code_required === true || accessCodeForced;

  const disabled = createAccount.isPending || tos.isPending || signIn.isPending;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    if (!tos.data) {
      const status = tos.error?.status ?? 0;
      const backendMessage = tos.error?.message ?? "unknown error";
      setError({
        code: "TOS_UNAVAILABLE",
        message: `Terms of Service could not be loaded (${backendMessage}). Retry the ToS load above, or check that the backend is running on :4000.`,
        status,
      });
      return;
    }
    if (!tosAccepted) {
      setError({
        code: "VALIDATION_FAILED",
        field: "tos",
        message: "Please accept the Terms of Service to continue.",
        status: 0,
      });
      return;
    }
    if (accessCodeRequired && accessCode.trim() === "") {
      setError({
        code: "VALIDATION_FAILED",
        field: "access_code",
        message: "Enter your access code to create an account.",
        status: 0,
      });
      return;
    }
    try {
      const outcome = await createAccount.mutateAsync({
        email,
        password,
        tos_version_label: tos.data.version_label,
        ...(accessCodeRequired ? { access_code: accessCode.trim() } : {}),
        ...(captchaToken ? { hcaptcha_token: captchaToken } : {}),
      });

      // The backend returns an already-verified account whenever its
      // deployment dismisses the verification step — the beta posture
      // (SIGNUP_EMAIL_VERIFICATION=dismissed, owner requirement 2026-09-03)
      // or local mock mode. Skip the check-your-email dead-end and sign the
      // user straight in so they land inside the app. Deployments that
      // require verification return email_verified=false and keep the
      // verify-email flow below.
      if (outcome.email_verified) {
        try {
          await signIn.mutateAsync({ email, password });
          await queryClient.invalidateQueries({ queryKey: ["account-me"] });
          toast.success("Account created. You're signed in.");
          navigate("/feed", { replace: true });
        } catch {
          // Auto-sign-in is a convenience; if it fails, drop the user on
          // the landing page to sign in by hand rather than stranding them.
          toast.success("Account created. Please sign in to continue.");
          navigate("/", { replace: true });
        }
        return;
      }

      toast.success("Account created. Check your email to verify.");
      navigate("/verify-email-pending", { replace: true });
    } catch (err) {
      const detail = err as CreateAccountError;
      if (detail.code === "invalid_access_code") {
        // Gate is active — make sure the field is visible for the retry.
        setAccessCodeForced(true);
      }
      // An hCaptcha token is single-use, so ANY failed attempt — captcha or
      // not — leaves a token the next submit cannot reuse. Clear it and reset
      // the widget so the retry produces a fresh one.
      setCaptchaToken("");
      try {
        captchaRef.current?.resetCaptcha();
      } catch {
        // The widget's script never loaded (offline, blocked, jsdom). There
        // is nothing to reset and nothing to report — the submit path already
        // treats a missing token as the server's decision.
      }
      setError(detail);
    }
  };

  const emailError =
    error?.field === "email"
      ? mapFieldError(error, "email")
      : error?.code === "EMAIL_ALREADY_REGISTERED"
        ? "That email is already registered."
        : null;
  const passwordError =
    error?.field === "password" ? mapFieldError(error, "password") : null;
  const tosError = error?.field === "tos" ? error.message : null;
  const accessCodeError =
    error?.code === "invalid_access_code"
      ? "That access code isn't valid. Check it and try again."
      : error?.field === "access_code"
        ? error.message
        : null;
  // Story 6.18 — the server's single fail-closed rejection, whatever caused
  // it (no token, hCaptcha said no, hCaptcha unreachable). Its message is
  // deliberately one message: the SPA must not be able to tell those apart
  // either.
  const captchaError = error?.field === "captcha" ? error.message : null;
  const generalError =
    emailError || passwordError || tosError || accessCodeError || captchaError
      ? null
      : mapGeneralError(error);

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-4"
      data-testid="create-account-form"
      noValidate
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="create-account-email" className="text-brand-paper-100">
          Email
        </Label>
        <Input
          id="create-account-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
          disabled={disabled}
          aria-describedby={emailError ? "create-account-email-error" : undefined}
          className="text-brand-forest-900"
        />
        {emailError ? (
          <p
            id="create-account-email-error"
            role="alert"
            className="text-sm text-state-error-accent"
          >
            {emailError}
            {error?.code === "EMAIL_ALREADY_REGISTERED" && onSwitchToSignIn ? (
              <>
                {" "}
                <button
                  type="button"
                  onClick={() => onSwitchToSignIn(email)}
                  className="underline hover:text-brand-gold-300"
                >
                  Sign in instead?
                </button>
              </>
            ) : null}
          </p>
        ) : null}
      </div>
      <div className="flex flex-col gap-1.5">
        <Label
          htmlFor="create-account-password"
          className="text-brand-paper-100"
        >
          Password
        </Label>
        <Input
          id="create-account-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          minLength={12}
          maxLength={128}
          required
          disabled={disabled}
          aria-describedby={
            passwordError
              ? "create-account-password-error"
              : "create-account-password-hint"
          }
          className="text-brand-forest-900"
        />
        <p
          id="create-account-password-hint"
          className="text-sm text-brand-paper-100/60"
        >
          12+ characters. Longer is better than fancier.
        </p>
        {passwordError ? (
          <p
            id="create-account-password-error"
            role="alert"
            className="text-sm text-state-error-accent"
          >
            {passwordError}
          </p>
        ) : null}
      </div>
      {accessCodeRequired ? (
        <div className="flex flex-col gap-1.5">
          <Label
            htmlFor="create-account-access-code"
            className="text-brand-paper-100"
          >
            Access code
          </Label>
          <Input
            id="create-account-access-code"
            type="text"
            value={accessCode}
            onChange={(e) => setAccessCode(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            required
            disabled={disabled}
            aria-describedby={
              accessCodeError
                ? "create-account-access-code-error"
                : "create-account-access-code-hint"
            }
            className="text-brand-forest-900"
            data-testid="create-account-access-code"
          />
          <p
            id="create-account-access-code-hint"
            className="text-sm text-brand-paper-100/60"
          >
            Signup is invite-only right now — enter the code you were given.
          </p>
          {accessCodeError ? (
            <p
              id="create-account-access-code-error"
              role="alert"
              className="text-sm text-state-error-accent"
            >
              {accessCodeError}
            </p>
          ) : null}
        </div>
      ) : null}
      {tos.isError ? (
        <div
          className="rounded border border-state-error-accent/60 bg-state-error-fill/10 p-3 text-sm text-brand-paper-100"
          role="alert"
        >
          <p className="m-0 mb-2">
            Couldn&apos;t load the Terms of Service ({tos.error.message}).
          </p>
          <button
            type="button"
            onClick={() => tos.refetch()}
            className="underline hover:text-brand-gold-300"
            data-testid="create-account-tos-retry"
          >
            Retry
          </button>
        </div>
      ) : null}
      <label className="flex items-start gap-2 text-sm text-brand-paper-100/80 cursor-pointer">
        <input
          type="checkbox"
          checked={tosAccepted}
          onChange={(e) => setTosAccepted(e.target.checked)}
          disabled={disabled || !tos.data}
          className="mt-0.5"
          aria-describedby={tosError ? "create-account-tos-error" : undefined}
          data-testid="create-account-tos"
        />
        <span>
          I&apos;ve read and accept the{" "}
          <a
            href="/legal/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-brand-gold-300"
          >
            Terms of Service
          </a>
          {tos.data ? ` (${tos.data.version_label})` : ""}.
        </span>
      </label>
      {tosError ? (
        <p
          id="create-account-tos-error"
          role="alert"
          className="text-sm text-state-error-accent"
        >
          {tosError}
        </p>
      ) : null}
      <div
        className="flex flex-col gap-1.5"
        data-testid="create-account-captcha"
      >
        <HCaptcha
          ref={captchaRef}
          sitekey={hcaptchaSiteKey()}
          theme="dark"
          onVerify={(token) => setCaptchaToken(token)}
          onExpire={() => setCaptchaToken("")}
          onError={() => setCaptchaToken("")}
        />
        {captchaError ? (
          <p
            id="create-account-captcha-error"
            role="alert"
            className="text-sm text-state-error-accent"
          >
            {captchaError}
          </p>
        ) : null}
      </div>
      {generalError ? (
        <p role="alert" className="text-sm text-state-error-accent">
          {generalError}
        </p>
      ) : null}
      <Button
        type="submit"
        variant="primary"
        disabled={disabled}
        aria-busy={disabled}
        className="w-full hover:brightness-110"
        data-testid="create-account-submit"
      >
        {createAccount.isPending ? "Creating account…" : "Create account"}
      </Button>
    </form>
  );
}

function mapFieldError(
  error: CreateAccountError,
  field: "email" | "password",
): string {
  if (error.code === "VALIDATION_FAILED") {
    if (field === "password") {
      return "Password must be at least 12 characters.";
    }
    if (field === "email") {
      return "Please enter a valid email address.";
    }
  }
  return error.message;
}

function mapGeneralError(error: CreateAccountError | null): string | null {
  if (!error) return null;
  switch (error.code) {
    case "RATE_LIMITED":
      return "Too many attempts. Try again in a few minutes.";
    case "TOS_UNAVAILABLE":
      return error.message;
    default:
      if (error.status >= 500) {
        const detail = error.message ? ` (${error.message})` : "";
        return `Something's not right on our end${detail}. Try again in a moment.`;
      }
      return error.message || "Registration failed. Try again.";
  }
}
